import React, { useState, useEffect } from "react";
import { string, object, func } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import WithSSO from "../../with-sso";
import { Link } from "@quintype/components";

import Button from "../../atoms/Button";
import LoginMobile from "../../atoms/icons/login";
import ContributorImage from "../../atoms/ContributorImage";
import PopupOverlay from "../../molecules/PopupOverlay";

import { logout, emailLink } from "../../helper/api";

import "./auth-view.m.css";
import { setMember, setJwtToken, setBookmark } from "../../helper/actions";

const { BroadcastChannel } = require("broadcast-channel");

const AuthView = ({
  pageType,
  member,
  domainSlug,
  navigateToPage,
  currentPath,
  authHost,
  publisherAttributes,
  borderColor,
  textColor,
  sketchesHost,
  setMember,
  setJwtToken,
  setBookmark,
}) => {
  const [callbackUrl, setCallbackUrl] = useState(sketchesHost);
  const [redirectUrl, setRedirectUrl] = useState(sketchesHost);
  const [showPopUp, setShowPopUp] = useState(false);

  const params = parseUrl(currentPath);

  const FallbackImage = () => <div styleName="first-letter">{member.email.charAt(0)}</div>;

  const getRedirectUrl = (defaultRedirectUrl) => {
    const redirectUrl = get(params, ["query", "redirect-url"], defaultRedirectUrl);
    setRedirectUrl(redirectUrl);
  };

  useEffect(() => {
    setCallbackUrl(get(params, ["query", "callback-url"], global.location.origin));
    if (
      params.url.includes("sign-in-page") ||
      params.url.includes("sign-up-page") ||
      params.url.includes("forgot-password")
    ) {
      getRedirectUrl(sketchesHost);
    } else {
      params.url.includes("verify-email") ? getRedirectUrl(sketchesHost) : getRedirectUrl(global.location.href);
    }
  }, [params]);

  const sendVerificationLink = () => {
    emailLink(member.email, `${redirectUrl}`).then(() => {
      setShowPopUp(true);
      localStorage.setItem("redirectUrl", window.location.href);
    });
  };

  const logoutClick = () => {
    logout().then(() => {
      const channel = new BroadcastChannel("logout-channel");
      channel.postMessage("listen for logout");
      setMember(null);
      setJwtToken(null);
      setBookmark(null);
      window.location.href = localStorage.getItem("redirectUrl");
      localStorage.removeItem("redirectUrl");
    });
  };

  const isLogoutType = () => {
    if (
      pageType === "profile-page" ||
      pageType === "edit-profile-page" ||
      pageType === "change-password-page" ||
      pageType === "my-comments" ||
      pageType === "saved-page"
    ) {
      return true;
    }

    return false;
  };

  const profilePageNavigation = () => {
    localStorage.setItem("redirectUrl", window.location.href);
    navigateToPage("/profile");
  };

  const changePopupView = () => setShowPopUp(false);

  // User not verified
  const notVerifiedView = () => {
    if (pageType === "verify-email-page") {
      return (
        <div styleName={`auth-wrapper ${publisherAttributes.lang}-wrapper`}>
          <Button
            content="Logout"
            buttonType="type4"
            onClick={logoutClick}
            className="btn logout-btn"
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
        </div>
      );
    }

    return (
      <React.Fragment>
        {/* Not Verified User Popup Button */}
        <div styleName={`auth-wrapper ${publisherAttributes.lang}-wrapper`}>
          <Button
            content="Verify Email"
            buttonType="type4"
            className="desktop-btn btn"
            onClick={sendVerificationLink}
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
          {/* Mobile Login Popup Button */}
          <div styleName="mobile-btn btn-wrapper not-verified" onClick={sendVerificationLink}>
            {member["avatar-url"] ? <ContributorImage authorObj={member} memberProfilePic={true} /> : FallbackImage()}
          </div>
        </div>
        {showPopUp && (
          <PopupOverlay
            lang={publisherAttributes.lang}
            popupMessage={`We sent an activation email to you at ${member.email}. Please check your email box.`}
            onClickHandler={changePopupView}
          />
        )}
      </React.Fragment>
    );
  };

  // Verified User
  const loggedInView = () => {
    if (isLogoutType()) {
      // Show logout button if its profile page
      return (
        <div styleName={`auth-wrapper ${publisherAttributes.lang}-wrapper`}>
          <Button
            content="Logout"
            buttonType="type4"
            onClick={logoutClick}
            className="btn logout-btn"
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
        </div>
      );
    }

    return (
      <div styleName={`auth-wrapper ${publisherAttributes.lang}-wrapper`}>
        <div href="/profile" onClick={profilePageNavigation} styleName="loggedin-button">
          <div styleName="btn-wrapper">
            {member["avatar-url"] ? <ContributorImage authorObj={member} memberProfilePic={true} /> : FallbackImage()}
          </div>
        </div>
      </div>
    );
  };

  // Not logged in User
  const loginView = () => (
    <React.Fragment>
      <WithSSO
        ssoHost={authHost}
        signInPath="/sign-in-page"
        signUpPath="/sign-up-page"
        redirectUrl={redirectUrl}
        callbackUrl={callbackUrl}
      >
        {({ signInHref }) => (
          <Link href={signInHref}>
            <div styleName={`auth-wrapper ${publisherAttributes.lang}-wrapper`}>
              <Button
                content="Login"
                buttonType="type4"
                className="desktop-btn btn"
                buttonStyle={{ borderColor: borderColor }}
                textStyle={{ color: textColor }}
                datatrackingcategory="Button"
                datatrackingaction="Click"
                datatrackinglabel="লগইন বাটন"
              />
              <div styleName="mobile-btn" className="mobile-btn">
                <LoginMobile
                  color={domainSlug === "kishoralo" ? "#121212" : "#555555"}
                  datatrackingcategory="Button"
                  datatrackingaction="Click"
                  datatrackinglabel="লগইন বাটন"
                />
              </div>
            </div>
          </Link>
        )}
      </WithSSO>
    </React.Fragment>
  );

  if (member) {
    return member["verification-status"] ? loggedInView() : notVerifiedView();
  }

  return loginView();
};

AuthView.propTypes = {
  pageType: string,
  member: object,
  navigateToPage: func,
  domainSlug: string,
  currentPath: string,
  authHost: string,
  publisherAttributes: object,
  sketchesHost: string,
  setMember: func,
  setJwtToken: func,
  setBookmark: func,
};

const mapStateToProps = (state) => ({
  currentPath: get(state, ["qt", "currentPath"], ""),
  pageType: get(state, ["qt", "pageType"], null),
  member: state.member || null,
  domainSlug: get(state, ["qt", "domainSlug"]),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
});

const mapDispatchToProps = (dispatch) => ({
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setMember: (obj) => dispatch(setMember(obj)),
  setJwtToken: (flag) => dispatch(setJwtToken(flag)),
  setBookmark: (obj) => dispatch(setBookmark(obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthView);
